// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback, useEffect } from 'react';
import { providerSubmissionApi } from '../api/providerSubmissionApi';
import type { ProviderAccessTokenDTO } from '../types/providerAccess';

interface UseAccessTokensReturn {
  data: ProviderAccessTokenDTO[] | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  revokeToken: (tokenId: string) => Promise<boolean>;
  revoking: boolean;
}

export function useAccessTokens(policyId: string): UseAccessTokensReturn {
  const [data, setData] = useState<ProviderAccessTokenDTO[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);

  const refresh = useCallback(async () => {
    if (!policyId) return;
    try {
      setLoading(true);
      setError(null);
      const tokens = await providerSubmissionApi.listAccessTokens(policyId);
      setData(tokens);
    } catch (err) {
      console.error('Failed to load access tokens:', err);
      setError('Failed to load access tokens');
    } finally {
      setLoading(false);
    }
  }, [policyId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const revokeToken = useCallback(
    async (tokenId: string): Promise<boolean> => {
      try {
        setRevoking(true);
        await providerSubmissionApi.revokeToken(policyId, tokenId);
        await refresh();
        return true;
      } catch (err) {
        console.error('Failed to revoke token:', err);
        setError('Failed to revoke token');
        return false;
      } finally {
        setRevoking(false);
      }
    },
    [policyId, refresh],
  );

  return { data, loading, error, refresh, revokeToken, revoking };
}
